import React, { useContext, useEffect, useState } from "react";
import AddMedicine from "./AddMedicine";
import Modal from "../../components/Modal";
import { approveMedicine, exportMedicines, getMedicines, mergeMedicine } from "../../apis/medicine";
import { Medicine } from "../../interfaces/Medicine";
import Filters from "../../components/Filters";
import { Filter } from "../../interfaces/Filter";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { LanguageContext } from "../../providers/LanguageProvider";
import CommonPagination from "../../components/CommanPagination";
import { format } from "date-fns";
import { Select, Tag } from "antd";
import { useNotification } from "../../NotificationContext";
import MastersBreadcrumb from "../../components/MastersBreadcrumb";

type Tab = "approved" | "pending_review";

const MedicineList = () => {
  const [editableId, setEditableId] = useState(null);
  const [meta, setMeta] = useState<any>();
  const [isMedicineModalOpen, setMedicineModalVisibility] = useState<boolean>(false);
  const { translate: t } = useContext(LanguageContext);
  const [medicines, setMedicines] = useState<Array<Medicine>>([]);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { showNotification } = useNotification();

  const [activeTab, setActiveTab] = useState<Tab>("approved");
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergingMedicine, setMergingMedicine] = useState<any>(null);
  const [mergeSearchResults, setMergeSearchResults] = useState<any[]>([]);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  const medicineFilters: Array<Filter> = [
    { label: "Company Name", key: "company_name", value: "", component: "input" },
    { label: "Form", key: "form", value: "", component: "input" },
  ];

  const [filters] = useState<Array<Filter>>(medicineFilters);
  const [isClearFilterList] = useState(false);
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (activeTab === "pending_review") {
      fetchMedicines({ status: "pending_review", per_page: 50 });
      return;
    }
    if (search) {
      const parsed = JSON.parse(
        '{"' +
          decodeURI(search.substring(1))
            .replace(/\+/g, " ")
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      );
      fetchMedicines({ per_page: 10, ...parsed });
    } else {
      fetchMedicines({ per_page: 10 });
    }
  }, [search, activeTab]);

  const handleFilters = (params: any) => {
    if (params.length > 0) {
      let updatedQueryParams = new URLSearchParams();
      params.forEach((i: any) => { updatedQueryParams.set(i.key, i.value); });
      setSearchParams(updatedQueryParams.toString());
    } else {
      navigate(`/medicines`);
    }
  };

  const handleCloseModal = () => {
    setEditableId(null);
    setMedicineModalVisibility(false);
    fetchMedicines();
  };

  const fetchMedicines = (params: any = {}) => {
    getMedicines(params).then((res: any) => {
      setMedicines(res.data.data);
      setMeta(res.data.meta);
    });
  };

  const handleEditMedicine = (payload: any) => {
    setEditableId(payload.id);
    setMedicineModalVisibility(true);
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchMedicines({ page, per_page: pageSize || 10 });
  };

  const downloadMedicinesList = () => {
    const params = search
      ? JSON.parse('{"' + decodeURI(search.substring(1)).replace(/\+/g, " ").replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}')
      : {};
    exportMedicines(params).then((res: any) => {
      const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "medicines-" + format(new Date(), "dd_MM_yyyy_hh_mm") + ".xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
  };

  const handleApprove = async (medicine: any) => {
    setActionLoading(medicine.id);
    try {
      await approveMedicine(medicine.id);
      showNotification("success", `${medicine.name} approved and added to global list.`, "", "topRight");
      fetchMedicines({ status: "pending_review", per_page: 50 });
    } catch {
      showNotification("error", "Failed to approve.", "", "topRight");
    } finally {
      setActionLoading(null);
    }
  };

  const openMergeModal = (medicine: any) => {
    setMergingMedicine(medicine);
    setMergeTargetId(null);
    setMergeSearchResults([]);
    setMergeModalOpen(true);
  };

  const searchMedicinesForMerge = (query: string) => {
    if (!query) return;
    getMedicines({ name: query, per_page: 20 }).then((res: any) => {
      setMergeSearchResults(res.data.data ?? []);
    });
  };

  const handleMergeConfirm = async () => {
    if (!mergingMedicine || !mergeTargetId) return;
    setMergeLoading(true);
    try {
      await mergeMedicine(mergingMedicine.id, mergeTargetId);
      showNotification("success", `Merged into target medicine. Pharmacy items re-linked.`, "", "topRight");
      setMergeModalOpen(false);
      fetchMedicines({ status: "pending_review", per_page: 50 });
    } catch {
      showNotification("error", "Merge failed.", "", "topRight");
    } finally {
      setMergeLoading(false);
    }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "approved", label: "Approved" },
    { key: "pending_review", label: "Pending Review" },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <MastersBreadcrumb current="Medicines" />
      {/* Header */}
      <div className="flex lg:hidden">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">{t("medicines")}</h1>
        </div>
        <div className="flex flex-row-reverse w-full gap-2">
          <button
            type="button"
            onClick={() => setMedicineModalVisibility(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
          <Filters clearFilterList={isClearFilterList} filters={filters} fetchList={(f: any) => handleFilters(f)} />
        </div>
      </div>

      <div className="hidden lg:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">{t("medicines")}</h1>
          <p className="mt-2 text-sm text-gray-700">{t("medicine_description")}</p>
        </div>
        <div className="mx-2 mt-4 sm:mt-0 sm:ml-16 sm:flex gap-2">
          <Filters clearFilterList={isClearFilterList} filters={filters} fetchList={(f: any) => handleFilters(f)} />
          <button
            type="button"
            onClick={() => setMedicineModalVisibility(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            {t("add_medicine")}
          </button>
          <button
            type="button"
            onClick={() => downloadMedicinesList()}
            className="block px-3 py-2 text-sm font-semibold text-center text-white capitalize rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            {t("export")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200">
        <nav className="flex -mb-px space-x-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
              {tab.key === "pending_review" && medicines.length > 0 && activeTab === "pending_review" && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                  {meta?.total ?? medicines.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="flow-root mt-4">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">{t("name")}</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t("molecule")}</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t("company")}</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t("form")}</th>
                    {activeTab === "pending_review" && (
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Source Clinic</th>
                    )}
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 capitalize">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {medicines && medicines.length > 0 ? (
                    medicines.map((medicine: any) => (
                      <tr key={medicine.id}>
                        <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-6">
                          {medicine.name}
                          {medicine.status === "pending_review" && (
                            <Tag color="orange" className="ml-2 text-xs">Pending</Tag>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 capitalize whitespace-nowrap">{medicine.molecule}</td>
                        <td className="px-3 py-4 text-sm text-gray-500 capitalize whitespace-nowrap">
                          {medicine.company ? medicine.company.name : ""}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 capitalize whitespace-nowrap">{medicine.form}</td>
                        {activeTab === "pending_review" && (
                          <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {medicine.sourceClinic?.name ?? "—"}
                          </td>
                        )}
                        <td className="relative py-4 pl-3 pr-4 text-sm font-medium text-left whitespace-nowrap sm:pr-6">
                          <div className="flex gap-2">
                            {activeTab === "approved" && (
                              <button
                                onClick={() => handleEditMedicine(medicine)}
                                className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
                              >
                                {t("edit")}
                              </button>
                            )}
                            {activeTab === "pending_review" && (
                              <>
                                <button
                                  disabled={actionLoading === medicine.id}
                                  onClick={() => handleApprove(medicine)}
                                  className="px-3 py-1.5 text-sm font-semibold text-white rounded-md bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                >
                                  {actionLoading === medicine.id ? "…" : "Approve"}
                                </button>
                                <button
                                  disabled={actionLoading === medicine.id}
                                  onClick={() => openMergeModal(medicine)}
                                  className="px-3 py-1.5 text-sm font-semibold text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                  Merge
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={activeTab === "pending_review" ? 6 : 5} className="py-4 pl-4 pr-3 text-sm font-medium text-center text-gray-900 sm:pl-6">
                        {activeTab === "pending_review" ? "No pending medicines — all clear!" : t("Record Not Found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      {/* Add/Edit modal */}
      {isMedicineModalOpen && (
        <Modal
          title={editableId ? "Update Medicine" : "Add Medicine"}
          onClose={() => handleCloseModal()}
          modalContent={<AddMedicine editableId={editableId} closeModal={() => handleCloseModal()} />}
        />
      )}

      {/* Merge modal */}
      {mergeModalOpen && mergingMedicine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Merge Medicine</h2>
            <p className="text-sm text-gray-500 mb-4">
              Merging <strong>{mergingMedicine.name}</strong> will re-link all pharmacy items to the selected target medicine, then delete this pending entry.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search target medicine to merge into</label>
            <Select
              showSearch
              placeholder="Type medicine name…"
              style={{ width: "100%" }}
              filterOption={false}
              onSearch={searchMedicinesForMerge}
              onChange={(val) => setMergeTargetId(val)}
              options={mergeSearchResults
                .filter((m) => m.id !== mergingMedicine.id)
                .map((m) => ({ value: m.id, label: `${m.name}${m.form ? " · " + m.form : ""}` }))}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setMergeModalOpen(false)}
                className="px-3 py-2 text-sm font-semibold text-gray-700 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={!mergeTargetId || mergeLoading}
                onClick={handleMergeConfirm}
                className="px-3 py-2 text-sm font-semibold text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {mergeLoading ? "Merging…" : "Confirm Merge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicineList;
